import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import dayjs from 'dayjs';

// Importar utilitários de PDF
import { checkPdfLibraries, createPdf } from '../utils/pdfUtils';

// Importar geradores de PDF
import PDFGenerator from '../components/relatorios/PDFGenerator';

const PdfDebug = () => {
  const [loading, setLoading] = useState(false);
  const [logMessages, setLogMessages] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Adicionar mensagem ao log
  const addLog = (message, type = 'info') => {
    setLogMessages(prev => [
      { id: Date.now(), message, type, timestamp: new Date() },
      ...prev
    ]);
  };
  
  // Limpar log
  const clearLog = () => {
    setLogMessages([]);
    setError(null);
    setSuccess(null);
  };
  
  // Testar biblioteca jsPDF básica
  const testBasicJsPdf = () => {
    try {
      clearLog();
      setLoading(true);
      addLog('Testando jsPDF básico...', 'info');
      
      // Verificar se jsPDF está disponível
      if (typeof jsPDF !== 'function') {
        throw new Error('jsPDF não está disponível');
      }
      
      // Criar documento simples
      const pdf = new jsPDF();
      addLog('Instância jsPDF criada com sucesso', 'success');
      
      // Adicionar texto simples
      pdf.text('Teste de PDF', 10, 10);
      addLog('Adicionado texto ao PDF', 'success');
      
      // Verificar se autoTable está disponível
      if (typeof pdf.autoTable === 'function') {
        addLog('Função autoTable está disponível', 'success');
      } else {
        addLog('Função autoTable NÃO está disponível', 'warning');
      }
      
      // Salvar o PDF
      const filename = `teste_basico_${Date.now()}.pdf`;
      pdf.save(filename);
      addLog(`PDF salvo como ${filename}`, 'success');
      
      setSuccess('Teste básico concluído com sucesso!');
    } catch (error) {
      console.error('Erro no teste básico:', error);
      addLog(`Erro: ${error.message}`, 'error');
      setError(`Erro no teste básico: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Testar utilitário de PDF
  const testPdfUtils = () => {
    try {
      clearLog();
      setLoading(true);
      addLog('Testando utilitários de PDF...', 'info');
      
      // Verificar bibliotecas
      const librariesOk = checkPdfLibraries();
      if (librariesOk) {
        addLog('Bibliotecas de PDF estão disponíveis', 'success');
      } else {
        addLog('Problema com bibliotecas de PDF', 'warning');
      }
      
      // Criar PDF com utilitário
      const pdf = createPdf();
      addLog('PDF criado com utilitário', 'success');
      
      // Verificar autoTable
      if (typeof pdf.autoTable === 'function') {
        addLog('Função autoTable está disponível no PDF criado', 'success');
        
        // Testar autoTable
        pdf.autoTable({
          head: [['Nome', 'Valor']],
          body: [
            ['Teste 1', '100'],
            ['Teste 2', '200'],
            ['Teste 3', '300']
          ]
        });
        
        addLog('Tabela criada com sucesso', 'success');
      } else {
        addLog('Função autoTable NÃO está disponível no PDF criado', 'error');
      }
      
      // Salvar o PDF
      const filename = `teste_utils_${Date.now()}.pdf`;
      pdf.save(filename);
      addLog(`PDF salvo como ${filename}`, 'success');
      
      setSuccess('Teste de utilitários concluído com sucesso!');
    } catch (error) {
      console.error('Erro no teste de utilitários:', error);
      addLog(`Erro: ${error.message}`, 'error');
      setError(`Erro no teste de utilitários: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Testar template do PDF
  const testPdfTemplate = () => {
    try {
      clearLog();
      setLoading(true);
      addLog('Testando template de PDF...', 'info');
      
      // Dados simulados para o template
      const dados = {
        distribuicaoPorServico: [
          { nome: 'Serviço 1', quantidade: 100, percentual: 50, tempoMedioAtendimento: 15, tempoMedioEspera: 10 },
          { nome: 'Serviço 2', quantidade: 50, percentual: 25, tempoMedioAtendimento: 20, tempoMedioEspera: 15 },
          { nome: 'Serviço 3', quantidade: 50, percentual: 25, tempoMedioAtendimento: 25, tempoMedioEspera: 20 }
        ],
        distribuicaoPorHorario: [
          { horario: '07:00', quantidade: 10 },
          { horario: '08:00', quantidade: 20 },
          { horario: '09:00', quantidade: 30 },
          { horario: '10:00', quantidade: 40 },
          { horario: '11:00', quantidade: 50 },
          { horario: '12:00', quantidade: 40 },
          { horario: '13:00', quantidade: 30 },
          { horario: '14:00', quantidade: 20 },
          { horario: '15:00', quantidade: 10 },
          { horario: '16:00', quantidade: 5 },
          { horario: '17:00', quantidade: 0 },
          { horario: '18:00', quantidade: 0 },
          { horario: '19:00', quantidade: 0 },
          { horario: '20:00', quantidade: 0 }
        ]
      };
      
      // Métricas simuladas
      const metricas = [
        { titulo: 'Senhas Emitidas', valor: 200, formato: 'numero' },
        { titulo: 'Senhas Finalizadas', valor: 180, formato: 'numero' },
        { titulo: 'Tempo Médio', valor: 18.5, formato: 'tempo' },
        { titulo: 'Senhas Canceladas', valor: 20, formato: 'numero' }
      ];
      
      // Gerar PDF com o gerador simplificado
      PDFGenerator.gerarPDF({
        tipoRelatorio: 'geral',
        dataInicio: dayjs().subtract(7, 'day'),
        dataFim: dayjs(),
        metricasPrincipais: metricas,
        onSuccess: (mensagem) => {
          addLog(mensagem, 'success');
          setSuccess('PDF gerado com sucesso usando método simplificado!');
          setLoading(false);
        },
        onError: (mensagem) => {
          addLog(mensagem, 'error');
          setError(mensagem);
          setLoading(false);
        }
      });
      
    } catch (error) {
      console.error('Erro no teste de template:', error);
      addLog(`Erro: ${error.message}`, 'error');
      setError(`Erro no teste de template: ${error.message}`);
      setLoading(false);
    }
  };
  
  // Testar gerador de PDF completo
  const testFullPdfGenerator = () => {
    try {
      clearLog();
      setLoading(true);
      addLog('Testando gerador de PDF completo...', 'info');
      
      // Métricas simuladas
      const metricas = [
        { titulo: 'Senhas Emitidas', valor: 200, formato: 'numero' },
        { titulo: 'Senhas Finalizadas', valor: 180, formato: 'numero' },
        { titulo: 'Tempo Médio', valor: 18.5, formato: 'tempo' },
        { titulo: 'Senhas Canceladas', valor: 20, formato: 'numero' }
      ];
      
      // Gerar PDF com o gerador completo
      PDFGenerator.gerarPDF({
        tipoRelatorio: 'geral',
        dataInicio: dayjs().subtract(7, 'day'),
        dataFim: dayjs(),
        metricasPrincipais: metricas,
        onSuccess: (mensagem) => {
          addLog(mensagem, 'success');
          setSuccess('Gerador de PDF completo funcionou com sucesso!');
          setLoading(false);
        },
        onError: (mensagem) => {
          addLog(mensagem, 'error');
          setError(mensagem);
          setLoading(false);
        }
      });
      
    } catch (error) {
      console.error('Erro no teste do gerador completo:', error);
      addLog(`Erro: ${error.message}`, 'error');
      setError(`Erro no teste do gerador completo: ${error.message}`);
      setLoading(false);
    }
  };
  
  // Renderizar ícone com base no tipo de mensagem
  const renderLogIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      default:
        return <InfoIcon color="info" />;
    }
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Debug de Geração de PDF
        </Typography>
        <Typography variant="body1" paragraph>
          Esta página permite testar os vários componentes de geração de PDF para diagnóstico de problemas.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Button 
            variant="contained" 
            onClick={testBasicJsPdf}
            disabled={loading}
            startIcon={<PdfIcon />}
          >
            Testar jsPDF Básico
          </Button>
          
          <Button 
            variant="contained" 
            onClick={testPdfUtils}
            disabled={loading}
            startIcon={<PdfIcon />}
          >
            Testar Utilitários
          </Button>
          
          <Button 
            variant="contained" 
            onClick={testPdfTemplate}
            disabled={loading}
            startIcon={<PdfIcon />}
          >
            Testar Template
          </Button>
          
          <Button 
            variant="contained" 
            onClick={testFullPdfGenerator}
            disabled={loading}
            startIcon={<PdfIcon />}
          >
            Testar Gerador Completo
          </Button>
          
          <Button 
            variant="outlined" 
            onClick={clearLog}
            disabled={loading}
          >
            Limpar Log
          </Button>
        </Box>
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        )}
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="h6" gutterBottom>
          Log de Operações
        </Typography>
        
        <List sx={{ bgcolor: '#f5f5f5', borderRadius: 1, maxHeight: 400, overflow: 'auto' }}>
          {logMessages.length === 0 ? (
            <ListItem>
              <ListItemText primary="Nenhuma operação registrada ainda" />
            </ListItem>
          ) : (
            logMessages.map((log) => (
              <ListItem key={log.id}>
                <ListItemIcon>
                  {renderLogIcon(log.type)}
                </ListItemIcon>
                <ListItemText 
                  primary={log.message}
                  secondary={log.timestamp.toLocaleTimeString()}
                />
                <Chip 
                  size="small" 
                  label={log.type}
                  color={
                    log.type === 'success' ? 'success' :
                    log.type === 'error' ? 'error' :
                    log.type === 'warning' ? 'warning' : 'info'
                  }
                />
              </ListItem>
            ))
          )}
        </List>
      </Paper>
    </Container>
  );
};

export default PdfDebug; 